/**
 * Growtype Analytics — Production Client-Side Tracker
 *
 * Auto-tracks pageviews via sendBeacon. Exposes `gaTrack()` for custom events.
 * 30-day session cookie. 10s pageview dedup. SPA pushState/popState support.
 * Anonymous users are stitched to logged-in users on wp_login via server-side hook.
 */
;(function () {
    'use strict';

    const ENDPOINT = '/wp-json/growtype-analytics/v1/track';
    const COOKIE_NAME = 'ga_sid';
    const COOKIE_DAYS = 30;
    const DEDUP_SECONDS = 10;
    const BATCH_DELAY_MS = 250;
    const MAX_BATCH_EVENTS = 25;

    var _lastPageview = { url: '', ts: 0 };
    var _sid = null;
    var _eventQueue = [];
    var _flushTimer = null;
    var _isActivated = !document.prerendering;
    var _activationEventQueue = [];
    var _activeExperimentKeys = Array.isArray(window.gaAbActiveExperiments)
        ? window.gaAbActiveExperiments.reduce(function (keys, key) {
            keys[String(key)] = true;
            return keys;
        }, {})
        : null;

    function isActiveExperiment(experiment) {
        return _activeExperimentKeys === null || !!_activeExperimentKeys[experiment];
    }

    function expireInactiveExperimentCookies() {
        if (_activeExperimentKeys === null || !document.cookie) return;
        document.cookie.split('; ').forEach(function (entry) {
            var separator = entry.indexOf('=');
            var name = separator >= 0 ? entry.substring(0, separator) : entry;
            var experiment = '';
            if (name.indexOf('ga_ab_v_') === 0) {
                experiment = name.substring('ga_ab_v_'.length);
            } else if (name.indexOf('ga_ab_') === 0 && name.indexOf('ga_ab_rc_') !== 0) {
                experiment = name.substring('ga_ab_'.length);
            }
            if (experiment && !isActiveExperiment(experiment)) {
                document.cookie = name + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax' + (window.location.protocol === 'https:' ? '; Secure' : '');
            }
        });
    }

    expireInactiveExperimentCookies();

    if (!_isActivated) {
        document.addEventListener('prerenderingchange', function () {
            _isActivated = true;
            while (_activationEventQueue.length) {
                var pending = _activationEventQueue.shift();
                sendEvent(pending.eventType, pending.data);
            }
        }, {once: true});
    }

    // --- Session ---
    function getSessionId() {
        if (_sid) return _sid;
        var m = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
        return m ? m[1] : '';
    }

    function createSessionId() {
        return 's' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    }

    function ensureSessionId() {
        if (!_sid) {
            _sid = getSessionId();
            if (!_sid) {
                _sid = createSessionId();
                var d = new Date();
                d.setTime(d.getTime() + COOKIE_DAYS * 86400000);
                document.cookie = COOKIE_NAME + '=' + _sid + '; path=/; expires=' + d.toUTCString() + '; SameSite=Lax';
            }
        }
        return _sid;
    }

    function getSharedAttribution() {
        var metadata = {};
        var cookies = document.cookie ? document.cookie.split('; ') : [];
        var assignments = {};

        cookies.forEach(function (entry) {
            var separator = entry.indexOf('=');
            var name = separator >= 0 ? entry.substring(0, separator) : entry;
            var value = separator >= 0 ? entry.substring(separator + 1) : '';

            try {
                value = decodeURIComponent(value);
            } catch (e) {}

            if (name === 'ga_attribution_context') {
                try {
                    var attribution = JSON.parse(value);
                    Object.keys(attribution || {}).forEach(function (key) {
                        if (/^[a-z0-9_]+$/i.test(key) && ['string', 'number', 'boolean'].indexOf(typeof attribution[key]) !== -1) {
                            metadata[key] = attribution[key];
                        }
                    });
                } catch (e) {}
                return;
            }

            if (name.indexOf('ga_ab_') === 0 && name.indexOf('ga_ab_rc_') !== 0 && name.indexOf('ga_ab_v_') !== 0) {
                var experiment = name.substring('ga_ab_'.length);
                if (experiment && isActiveExperiment(experiment)) {
                    metadata['ab_' + experiment] = value;
                    assignments[experiment] = value;
                }
            }
        });

        var experiments = Object.keys(assignments);
        if (experiments.length) {
            metadata.experiment_assignments = JSON.stringify(assignments);
            if (experiments.length === 1) {
                metadata.experiment = experiments[0];
                metadata.variant = assignments[experiments[0]];
            }
        }

        return metadata;
    }

    // --- Send ---
    function sendBatch(events) {
        if (!events.length) return;

        var body = JSON.stringify({ events: events });
        try {
            if (navigator.sendBeacon) {
                var blob = new Blob([body], { type: 'application/json' });
                if (navigator.sendBeacon(ENDPOINT, blob)) return;
            }

            if (typeof fetch === 'function') {
                fetch(ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: body,
                    credentials: 'same-origin',
                    keepalive: true,
                }).catch(function () {});
                return;
            }

            var xhr = new XMLHttpRequest();
            xhr.open('POST', ENDPOINT, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.timeout = 3000;
            xhr.send(body);
        } catch (e) {
            // Silently fail — analytics must never break the page
        }
    }

    function flushQueue() {
        if (_flushTimer) {
            clearTimeout(_flushTimer);
            _flushTimer = null;
        }

        while (_eventQueue.length) {
            sendBatch(_eventQueue.splice(0, MAX_BATCH_EVENTS));
        }
    }

    function queueEvent(payload) {
        _eventQueue.push(payload);
        if (_eventQueue.length >= MAX_BATCH_EVENTS) {
            flushQueue();
            return;
        }
        if (!_flushTimer) {
            _flushTimer = setTimeout(flushQueue, BATCH_DELAY_MS);
        }
    }

    function sendEvent(eventType, data) {
        data = data || {};

        if (!_isActivated) {
            _activationEventQueue.push({eventType: eventType, data: data});
            return;
        }

        if (eventType === 'pageview') {
            var url = window.location.pathname + window.location.search;
            var now = Date.now();
            if (_lastPageview.url === url && (now - _lastPageview.ts) < DEDUP_SECONDS * 1000) return;
            _lastPageview = { url: url, ts: now };
        }

        var payload = {
            event_type: eventType,
            object_id: data.object_id || '',
            object_type: data.object_type || '',
            session_id: ensureSessionId(),
            page_url: window.location.pathname + window.location.search,
            metadata: Object.assign({}, getSharedAttribution(), {
                referrer: document.referrer || '',
                title: document.title || '',
            }, data.metadata || {}),
        };

        queueEvent(payload);
        // Debug panel
        if (window.gaDebug) addDebugEntry(eventType, payload.page_url);
    }

    var _debugEntries = [];
    var _debugPanel = null;

    function addDebugEntry(type, url) {
        var now = new Date();
        var ts = now.toTimeString().substr(0, 8);
        _debugEntries.push({ ts: ts, type: type, url: url });
        if (_debugEntries.length > 50) _debugEntries.shift();
        updateDebugPanel();
    }

    function escapeHtml(str) {
        return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function updateDebugPanel() {
        if (!_debugPanel) return;
        var html = '';
        for (var i = _debugEntries.length - 1; i >= 0; i--) {
            var e = _debugEntries[i];
            html += '<div style="font-size:11px;padding:2px 0;border-bottom:1px solid #eee;font-family:monospace;">' +
                '<span style="color:#888;">' + escapeHtml(e.ts) + '</span> ' +
                '<span style="color:#2271b1;font-weight:600;">' + escapeHtml(e.type) + '</span> ' +
                '<span style="color:#555;">' + escapeHtml(e.url.substring(0, 50)) + '</span>' +
                '</div>';
        }
        _debugPanel.querySelector('.ga-debug-body').innerHTML = html || '<div style="color:#888;font-size:11px;">Waiting for events...</div>';
    }

    function initDebugPanel() {
        if (document.getElementById('ga-debug-panel')) return;
        var panel = document.createElement('div');
        panel.id = 'ga-debug-panel';
        panel.innerHTML = '<div style="background:#1d2327;color:#fff;padding:6px 10px;font-size:11px;font-weight:700;display:flex;justify-content:space-between;align-items:center;border-radius:8px 8px 0 0;cursor:pointer;" onclick="var b=this.nextElementSibling;b.style.display=b.style.display===\'none\'?\'block\':\'none\';this.querySelector(\'.ga-debug-toggle\').textContent=b.style.display===\'none\'?\'▶\':\'▼\';">' +
            '🐞 GA Tracker <span class="ga-debug-toggle">▼</span>' +
            '<span style="cursor:pointer;margin-left:8px;" onclick="event.stopPropagation();document.getElementById(\'ga-debug-panel\').remove();">✕</span>' +
            '</div>' +
            '<div class="ga-debug-body" style="max-height:200px;overflow-y:auto;background:#fff;padding:6px 10px;border-radius:0 0 8px 8px;border:1px solid #e2e8f0;border-top:0;">' +
            '<div style="color:#888;font-size:11px;">Waiting for events...</div>' +
            '</div>';
        panel.style.cssText = 'position:fixed;bottom:12px;right:12px;z-index:99999;width:320px;box-shadow:0 4px 20px rgba(0,0,0,0.15);border-radius:8px;';
        document.body.appendChild(panel);
        _debugPanel = panel;
        updateDebugPanel();
    }

    // --- Track page view ---
    function trackPageView() {
        if (window.location.pathname.indexOf('/wp-admin') === 0 ||
            window.location.pathname.indexOf('/wp-json') === 0) return;
        sendEvent('pageview', { object_id: window.location.pathname, object_type: 'page' });
    }

    // --- Init ---
    function onReady(fn) {
        if (document.readyState !== 'loading') fn();
        else document.addEventListener('DOMContentLoaded', fn);
    }

    function initializeTracker() {
        onReady(function () {
        if (window.gaDebug) initDebugPanel();
        trackPageView();

        // Outbound link clicks
        document.addEventListener('click', function (e) {
            var link = e.target.closest('a');
            if (!link || !link.href) return;
            try {
                var url = new URL(link.href, window.location.origin);
                if (url.hostname !== window.location.hostname) {
                    sendEvent('outbound_click', {
                        object_id: link.href,
                        object_type: 'link',
                        metadata: { link_text: (link.textContent || '').trim().substring(0, 100) },
                    });
                }
            } catch (e) {}
        });

        // SPA navigation: track on popstate (back/forward)
        window.addEventListener('popstate', function () {
            // Small delay to let the URL update
            setTimeout(trackPageView, 50);
        });
        window.addEventListener('pagehide', flushQueue);
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'hidden') flushQueue();
        });
        });
    }

    if (document.prerendering) {
        document.addEventListener('prerenderingchange', initializeTracker, {once: true});
    } else {
        initializeTracker();
    }

    // --- Monkey-patch pushState/replaceState for SPA tracking ---
    var _pushState = history.pushState;
    var _replaceState = history.replaceState;
    history.pushState = function () {
        _pushState.apply(this, arguments);
        setTimeout(trackPageView, 50);
    };
    history.replaceState = function () {
        _replaceState.apply(this, arguments);
        setTimeout(trackPageView, 50);
    };

    // --- Public API ---
    window.gaTrack = function (eventType, data) {
        sendEvent(eventType, data);
    };
})();
